import { inject, Injectable } from '@angular/core';
import { Play } from '@r1-compass-library/models';
import {
    ContentAssetService,
    LifecycleRulesetService,
    PlayService,
    TemplateCriteriaService,
} from '@r1-compass-library/services';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { catchError, defaultIfEmpty, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { PlayValidationContext, PlayValidationService } from './play-validation.service';
import { MatDialog } from '@angular/material/dialog';
import { Dependency } from '@r1-compass-library/models/dependencies.model';
import {
    DisplayAssetDependencies,
    DisplayAssetDependenciesListComponent,
} from '@r1-compass-library/components/display-asset-dependencies-list/display-asset-dependencies-list.component';

export type RevalidatableEntityType = 'templateCriteria' | 'lifecycleRuleset' | 'contentAsset';

@Injectable({
    providedIn: 'root',
})
export class PlayRevalidationService {
    readonly #playService = inject(PlayService);
    readonly #templateCriteriaService = inject(TemplateCriteriaService);
    readonly #lifecycleRulesetService = inject(LifecycleRulesetService);
    readonly #contentAssetService = inject(ContentAssetService);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #playValidationService = inject(PlayValidationService);
    readonly #dialog = inject(MatDialog);

    public revalidatePlaysForEntity(entityType: RevalidatableEntityType, entityId: number): Observable<Play[]> {
        const divisionConfig = this.#globalLoggedInUserStore.selectedDivisionConfig()!;
        const multipleRecommendationsEnabled =
            divisionConfig?.config?.mapping?.multipleRecommendations?.enabled ?? false;

        let playSearch$: Observable<Play[]>;
        if (entityType === 'templateCriteria') {
            playSearch$ = this.#playService.search({ templateCriteriaId: entityId, deleted: 'False', valid: 'False' });
        } else if (entityType === 'lifecycleRuleset') {
            playSearch$ = this.#playService.search({ lifecycleRulesetId: entityId, deleted: 'False', valid: 'False' });
        } else {
            playSearch$ = this.#playService.search({ contentAssetId: entityId, deleted: 'False', valid: 'False' });
        }

        return playSearch$.pipe(
            switchMap(plays => {
                if (plays.length === 0) {
                    return of<Play[]>([]);
                }

                const lifecycleIds = new Set<number>();
                const templateIds = new Set<number>();
                const contentAssetIds = new Set<number>();

                for (const play of plays) {
                    const lifecycleId = play.lifecycleRulesetId;
                    if (lifecycleId) {
                        lifecycleIds.add(lifecycleId);
                    }

                    const templateId = play.templateCriteriaId;
                    if (templateId) {
                        templateIds.add(templateId);
                    }

                    const assets = play.config?.talkingPointsDefinition?.contentAssets ?? [];
                    for (const asset of assets) {
                        if (asset?.id) {
                            contentAssetIds.add(asset.id);
                        }
                    }
                }

                if (entityType === 'lifecycleRuleset') {
                    lifecycleIds.delete(entityId);
                } else if (entityType === 'templateCriteria') {
                    templateIds.delete(entityId);
                } else if (entityType === 'contentAsset') {
                    contentAssetIds.delete(entityId);
                }

                const fetchLifecycles$ =
                    lifecycleIds.size > 0
                        ? this.#lifecycleRulesetService.search({ ids: Array.from(lifecycleIds), deleted: 'False' })
                        : of([]);

                const fetchTemplates$ =
                    templateIds.size > 0
                        ? this.#templateCriteriaService.search({ ids: Array.from(templateIds), deleted: 'False' })
                        : of([]);

                const fetchContentAssets$ =
                    contentAssetIds.size > 0
                        ? this.#contentAssetService.search({ ids: Array.from(contentAssetIds), deleted: 'False' })
                        : of([]);

                return forkJoin({
                    lifecycleRulesets: fetchLifecycles$,
                    templateCriteria: fetchTemplates$,
                    contentAssets: fetchContentAssets$,
                }).pipe(
                    switchMap(({ lifecycleRulesets, templateCriteria, contentAssets }) => {
                        const isUnavailable = (asset: { enabled?: boolean; valid?: boolean }) =>
                            !asset.enabled || !asset.valid;

                        const unavailableLifecycleRulesetIds = new Set(lifecycleIds);
                        lifecycleRulesets
                            .filter(r => !isUnavailable(r))
                            .forEach(r => unavailableLifecycleRulesetIds.delete(r.id));

                        const unavailableTemplateCriteriaIds = new Set(templateIds);
                        templateCriteria
                            .filter(c => !isUnavailable(c))
                            .forEach(c => unavailableTemplateCriteriaIds.delete(c.id));

                        const unavailableLibraryContentIds = new Set(contentAssetIds);
                        contentAssets
                            .filter(c => !isUnavailable(c))
                            .forEach(c => unavailableLibraryContentIds.delete(c.id));

                        const context: PlayValidationContext = {
                            unavailableLifecycleRulesetIds,
                            unavailableTemplateCriteriaIds,
                            unavailableLibraryContentIds,
                            usedLibraryContentIds: new Set(),
                            multipleRecommendationsEnabled,
                            selectedDivisionConfig: divisionConfig,
                        };

                        const nowValidPlays = this.#getNowValidPlays(plays, context);
                        if (nowValidPlays.length === 0) {
                            return of<Play[]>([]);
                        }

                        return this.#updatePlays(nowValidPlays, entityId);
                    })
                );
            }),
            catchError(err => {
                console.error('Failed to revalidate plays for entity:', err);
                return of<Play[]>([]);
            }),
            defaultIfEmpty([])
        );
    }

    #getNowValidPlays(plays: Play[], context: PlayValidationContext) {
        return plays.filter(play => {
            const status = this.#playValidationService.validateModel(play, context);
            return status.erroredSections.size === 0;
        });
    }

    #updatePlays(nowValidPlays: Play[], entityId: number) {
        const validPlayIds = nowValidPlays.map(p => p.id);

        return this.#playService.patchPlays({ ids: validPlayIds }, { valid: true }).pipe(
            tap(() => this.#displayUpdatedPlays(nowValidPlays, entityId)),
            catchError(err => {
                console.error('Failed to patch plays to valid:', err);
                return of<Play[]>([]);
            })
        );
    }

    #displayUpdatedPlays(nowValidPlays: Play[], entityId: number) {
        const dependencies: Dependency[] = nowValidPlays.map(play => ({
            playId: play.id,
            name: play.name,
            assetId: entityId,
        }));
        this.#dialog.open<DisplayAssetDependenciesListComponent, DisplayAssetDependencies>(
            DisplayAssetDependenciesListComponent,
            {
                width: '600px',
                disableClose: true,
                data: {
                    dependencies,
                    dialogText: `The following play${nowValidPlays.length > 1 ? 's were' : ' was'} updated to valid:`,
                    dialogTitle: 'Plays Updated',
                    cancelButtonText: 'Close',
                },
                autoFocus: false,
            }
        );
    }
}
